/**
 * Whether this page is behind the deployed one, whether that has cost it
 * anything yet, and whether it may reload itself.
 *
 * A tab left open across a deploy keeps every asset it already loaded and loses
 * the ones it has yet to fetch, since each is named by a content hash the
 * deploy replaced. Two different questions follow from that, and they deserve
 * different answers because acting on them costs different amounts:
 *
 *   isBuildStale()       has anything at all shipped since this page loaded?
 *                        Broad and cheap. Only ever used for the reload nobody
 *                        sees: a hidden tab taking the new version costs a page
 *                        load and no attention, so there is no reason to be
 *                        precise about it. Converging tabs this way is also what
 *                        keeps the *other* hashed chunks — spell data, routes —
 *                        from going missing under a long-lived page.
 *
 *   isLocalSolverLost()  can this page still start a solver island?
 *                        Narrow, and asked by trying. This one is allowed to
 *                        interrupt someone, so it has to mean something: chunk
 *                        names are content hashes, and a deploy that leaves the
 *                        worker alone leaves the page solving perfectly.
 *                        Measured here, a one-line edit to an unrelated page
 *                        reissued the entry bundle and left all 25 other chunks
 *                        untouched, solver included. Prompting on that would be
 *                        20 interruptions a day over nothing.
 */
import { islandsReachable } from './localSolve/pool';

/**
 * Floor between two checks of the same kind. The answer only changes when we
 * deploy, and the callers are events the user generates freely — returning to
 * the tab, pressing generate.
 */
const RECHECK_MS = 60_000;

/** Where the deployed entry bundle is named, and how to recognise it. */
const INDEX_URL = '/index.html';
const ENTRY_RE = /\/static\/js\/main\.[A-Za-z0-9_-]+\.js/;

/** Remembers the build a reload was already attempted from. */
const RELOAD_MARK = 'dofgen.reloadedFrom';

/**
 * The entry bundle this page is running.
 *
 * Read from the DOM rather than from a build-time constant, so there is nothing
 * to keep in sync and no way for the two to disagree — the same reason the
 * kernel digest is published beside its assets instead of compiled in.
 *
 * Empty when it cannot be told, which is the case under `npm start` (the dev
 * server serves an unhashed bundle). Then staleness is simply never claimed,
 * which is the right answer for a dev server that hot-reloads anyway.
 */
const runningEntry = (() => {
    if (typeof document === 'undefined') return '';
    const el = document.querySelector('script[src*="/static/js/main."]');
    if (!el) return '';
    const match = ENTRY_RE.exec(el.getAttribute('src') || '');
    return match ? match[0] : '';
})();

let staleKnown = false;
let staleCheckedAtMs = 0;
let staleInFlight = null;

let lostKnown = false;
let lostCheckedAtMs = 0;
let lostInFlight = null;

/** Generations in progress, which a reload would throw away. */
let busyCount = 0;

/**
 * Whether a newer build than this page is deployed.
 *
 * One conditional request for the document, whose own script tags name the
 * current entry bundle. Sticky once true: deploys do not un-happen.
 *
 * @returns {Promise<boolean>}
 */
export async function isBuildStale() {
    if (staleKnown) return true;
    if (!runningEntry || typeof fetch === 'undefined') return false;

    const now = Date.now();
    if (staleCheckedAtMs && now - staleCheckedAtMs < RECHECK_MS) return false;
    if (staleInFlight) return staleInFlight;

    staleInFlight = (async () => {
        try {
            // Revalidated, not read from cache: this document is the one thing
            // whose URL does not change when its content does.
            const res = await fetch(INDEX_URL, { cache: 'no-cache' });
            if (!res.ok) return false;
            const match = ENTRY_RE.exec(await res.text());
            // No match means the deployed document is shaped in a way this does
            // not understand. Staying quiet is the safe reading of that.
            if (!match) return false;
            staleKnown = match[0] !== runningEntry;
            return staleKnown;
        } catch (_err) {
            // Offline, or a request that never landed. Says nothing about deploys.
            return false;
        } finally {
            staleCheckedAtMs = Date.now();
            staleInFlight = null;
        }
    })();

    return staleInFlight;
}

/**
 * Whether this page can no longer load its solver.
 *
 * Cheap to call: cached for `RECHECK_MS`, concurrent callers share one probe,
 * and once lost it stays lost — nothing but a reload brings back a URL that is
 * gone. Note this measures capability, not what is deployed: a worker still
 * served from cache answers reachable, and rightly so, because generating
 * locally will work.
 *
 * @returns {Promise<boolean>}
 */
export async function isLocalSolverLost() {
    if (lostKnown) return true;
    if (typeof Worker === 'undefined') return false;

    const now = Date.now();
    if (lostCheckedAtMs && now - lostCheckedAtMs < RECHECK_MS) return false;
    if (lostInFlight) return lostInFlight;

    lostInFlight = (async () => {
        try {
            lostKnown = !await islandsReachable();
            return lostKnown;
        } catch (_err) {
            // A probe that cannot even fail properly says nothing worth acting on.
            return false;
        } finally {
            lostCheckedAtMs = Date.now();
            lostInFlight = null;
        }
    })();

    return lostInFlight;
}

/**
 * Record what a run already discovered, so the UI need not probe again.
 *
 * A generation that fell back because its islands never answered has just run
 * this exact check, at length and for real.
 */
export function noteLocalSolverLost() {
    lostKnown = true;
    lostCheckedAtMs = Date.now();
}

/**
 * Whether an unattended reload may be attempted, and from where.
 *
 * Guards the one failure mode a self-reloading page must not have. If anything
 * makes the check read stale forever — a cached document naming an entry bundle
 * that is not what gets served, say — a page that reloads on staleness reloads
 * on every load, and the tab is unusable. So a reload is attempted at most once
 * per build: come back still running the same entry bundle and it stands down.
 */
export function mayAutoReload() {
    if (!runningEntry) return false;
    try {
        return window.sessionStorage.getItem(RELOAD_MARK) !== runningEntry;
    } catch (_err) {
        // Storage denied. Not knowing whether we already tried means not trying.
        return false;
    }
}

/** Claim the one reload attempt this build gets. */
export function noteAutoReload() {
    try {
        window.sessionStorage.setItem(RELOAD_MARK, runningEntry);
    } catch (_err) {
        // Nothing to do: `mayAutoReload` refuses when storage is unavailable.
    }
}

/**
 * Mark a generation as running, so nothing reloads the page underneath it.
 *
 * A counter rather than a flag: runs can overlap while a cancelled one winds
 * down, and the last to finish must not clear the first one's claim.
 */
export function markPageBusy(busy) {
    busyCount = Math.max(0, busyCount + (busy ? 1 : -1));
}

/** Whether reloading right now would interrupt something the user started. */
export function isPageBusy() {
    return busyCount > 0;
}

/** Reset for tests. */
export function resetPageHealth() {
    staleKnown = false;
    staleCheckedAtMs = 0;
    staleInFlight = null;
    lostKnown = false;
    lostCheckedAtMs = 0;
    lostInFlight = null;
    busyCount = 0;
}
